import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack
} from '@mui/material';
import {
  WorkOutline as WorkIcon,
  AccessTime as TimeIcon,
  CalendarToday as CalendarIcon,
  Share as ShareIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import {
  collection, addDoc, getDocs, query, orderBy, where, serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase';

function Career() {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    experience: '',
    message: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    console.log('Fetching jobs in Career page...');
    try {
      // First get all jobs
      const q = query(collection(db, 'jobs'));
      console.log('Query created:', q);
      const querySnapshot = await getDocs(q);
      console.log('Query snapshot:', querySnapshot.docs.length, 'documents found');
      
      // Then filter active jobs and sort them in memory
      const jobsData = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(job => job.status === 'active')
        .sort((a, b) => {
          if (!a.timestamp || !b.timestamp) return 0;
          return b.timestamp.seconds - a.timestamp.seconds;
        });

      console.log('Final jobs data:', jobsData);
      setJobs(jobsData);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setSnackbar({
        open: true,
        message: 'Error loading job listings. Please try again later.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApplyClick = (job) => {
    setSelectedJob(job);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      experience: '',
      message: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedJob) return;

    try {
      await addDoc(collection(db, 'applications'), {
        jobId: selectedJob.id,
        jobTitle: selectedJob.title,
        ...formData,
        timestamp: serverTimestamp(),
        status: 'pending'
      });

      setSnackbar({
        open: true,
        message: 'Application submitted successfully!',
        severity: 'success'
      });
      handleCloseDialog();
    } catch (error) {
      console.error('Error submitting application:', error);
      setSnackbar({
        open: true,
        message: 'Error submitting application. Please try again.',
        severity: 'error'
      });
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Recently';
    const date = timestamp.toDate();
    return new Intl.DateTimeFormat('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(date);
  };

  return (
    <Box sx={{ mt: '-64px', pt: '64px' }}>
      {/* Hero Banner */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '200px', md: '250px' },
          width: '100%',
          overflow: 'hidden',
          mb: 0,
          bgcolor: '#580100',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(/career-banner.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.3)',
            zIndex: 0
          }
        }}
      >
        <Container 
          maxWidth="lg"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            zIndex: 2
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            sx={{
              color: 'white',
              textAlign: 'center',
              fontWeight: 700,
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              fontSize: { xs: '2.5rem', md: '3.5rem' }
            }}
          >
            Career Opportunities
          </Typography>
        </Container>
      </Box>

      {/* Main Content */}
      <Box sx={{ py: { xs: 6, md: 8 }, background: '#fff' }}>
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            sx={{ color: '#580100', mb: 4 }}
          >
            Join Our Team
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ mb: 6 }}>
            Explore exciting career opportunities with the Andheri Court's Bar Association. 
            We're looking for talented individuals who are passionate about law and justice.
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : jobs.length > 0 ? (
            <Grid container spacing={3}>
              {jobs.map((job) => (
                <Grid item xs={12} key={job.id}>
                  <Card elevation={1} sx={{ '&:hover': { boxShadow: 3 } }}>
                    <CardContent sx={{ p: '16px !important' }}>
                      <Box>
                        {/* Header with title and buttons */}
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: { xs: 'column', sm: 'row' },
                          justifyContent: 'space-between',
                          alignItems: { xs: 'stretch', sm: 'center' },
                          mb: 2
                        }}>
                          {/* Title */}
                          <Typography 
                            variant="h5" 
                            component="h2" 
                            sx={{ 
                              color: '#580100',
                              fontSize: { xs: '1.25rem', sm: '1.5rem' },
                              mb: { xs: 2, sm: 0 },
                              lineHeight: 1.2
                            }}
                          >
                            {job.title}
                          </Typography>

                          {/* Action Buttons */}
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 1,
                            minWidth: { sm: '280px' },
                            justifyContent: { sm: 'flex-end' }
                          }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<ShareIcon />}
                              onClick={() => {
                                const shareText = `Job Opening at Andheri Court's Bar Association:\n\n${job.title}\n${job.type} | ${job.experience}\n\nApply now at: ${window.location.href}`;
                                window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');
                              }}
                              sx={{
                                borderColor: '#580100',
                                color: '#580100',
                                '&:hover': {
                                  borderColor: '#400000',
                                  bgcolor: 'rgba(88, 1, 0, 0.04)'
                                },
                                width: { xs: '100%', sm: 'auto' }
                              }}
                            >
                              Share on WhatsApp
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleApplyClick(job)}
                              sx={{
                                bgcolor: '#580100',
                                '&:hover': {
                                  bgcolor: '#400000'
                                },
                                width: { xs: '100%', sm: 'auto' }
                              }}
                            >
                              Apply
                            </Button>
                          </Box>
                        </Box>

                        {/* Job Meta Information */}
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: { xs: 1, sm: 2.5 },
                          mb: 2,
                          mt: 1
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <WorkIcon sx={{ color: '#580100', fontSize: '1rem' }} />
                            <Typography variant="body2" color="text.secondary">
                              {job.type}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <BusinessIcon sx={{ color: '#580100', fontSize: '1rem' }} />
                            <Typography variant="body2" color="text.secondary">
                              {job.experience}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TimeIcon sx={{ color: '#580100', fontSize: '1rem' }} />
                            <Typography variant="body2" color="text.secondary">
                              Posted: {formatDate(job.timestamp)}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Job Description */}
                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                          {job.description}
                        </Typography>

                        {/* Requirements Section */}
                        {job.requirements && job.requirements.length > 0 && (
                          <Box>
                            <Typography 
                              variant="subtitle1" 
                              sx={{ 
                                color: '#580100', 
                                mb: 1,
                                fontWeight: 600,
                                fontSize: '1rem'
                              }}
                            >
                              Requirements:
                            </Typography>
                            <List sx={{ 
                              pl: 2, 
                              pt: 0,
                              pb: 0,
                              '& .MuiListItem-root': {
                                py: 0.25
                              }
                            }}>
                              {job.requirements.map((requirement, index) => (
                                <ListItem 
                                  key={index} 
                                  sx={{ 
                                    display: 'list-item',
                                    listStyleType: 'disc',
                                  }}
                                >
                                  <Typography variant="body2">
                                    {requirement}
                                  </Typography>
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No job openings available at the moment.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Please check back later for new opportunities.
              </Typography>
            </Box>
          )}
        </Container>
      </Box>

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', color: '#580100' }}>
          Apply for {selectedJob?.title}
        </DialogTitle>
        
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ py: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Full Name"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  required
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone"
                  fullWidth
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Years of Experience"
                  fullWidth
                  required
                  value={formData.experience}
                  onChange={(e) => setFormData(prev => ({ ...prev, experience: e.target.value }))}
                  placeholder="e.g. 3 years"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Cover Letter"
                  multiline
                  rows={4}
                  fullWidth
                  required
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Tell us why you're interested in this position and what makes you a great candidate."
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
            <Button onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                bgcolor: '#580100',
                '&:hover': {
                  bgcolor: '#400000'
                }
              }}
            >
              Submit Application
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Career;
